import styled from 'styled-components'
import { StatCardVariant } from './StatCard.types'
import { media } from '../../../utils/mediaQuery'

export const Container = styled.div<{
  $variant?: StatCardVariant
  $highlight?: boolean
  $hasIcon?: boolean
  $disabled?: boolean
  $clickable?: boolean
}>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $variant, $highlight }) => {
      if ($highlight && $variant === 'warning') return theme.colors.warning
      if ($highlight && $variant === 'success') return theme.colors.success
      if ($highlight && $variant === 'danger') return theme.colors.danger
      if ($highlight && $variant === 'primary') return theme.colors.primary
      if ($variant === 'warning') return theme.colors.warning
      if ($variant === 'success') return theme.colors.success
      if ($variant === 'danger') return theme.colors.danger
      if ($variant === 'primary') return theme.colors.primary
      return theme.colors.border.default
    }};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ $hasIcon }) =>
    $hasIcon ? '16px 32px 24px 32px' : '24px 32px'};
  text-align: center;
  transition: ${({ theme }) => theme.transitions.default};
  min-width: 140px;
  position: relative;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  flex: auto;

  &:hover {
    border-color: ${({ theme, $variant, $highlight }) => {
      if ($highlight && $variant === 'warning') return theme.colors.warning
      if ($highlight && $variant === 'success') return theme.colors.success
      if ($highlight && $variant === 'danger') return theme.colors.danger
      if ($highlight && $variant === 'primary') return theme.colors.primary
      if ($variant === 'warning') return theme.colors.warning
      if ($variant === 'success') return theme.colors.success
      if ($variant === 'danger') return theme.colors.danger
      if ($variant === 'primary') return theme.colors.primary
      return theme.colors.text.tertiary
    }};
    box-shadow: ${({ theme, $variant, $highlight }) => {
      if ($highlight && $variant === 'warning')
        return '0 2px 8px rgba(255, 149, 0, 0.15)'
      if ($highlight && $variant === 'success')
        return '0 2px 8px rgba(0, 179, 65, 0.15)'
      if ($highlight && $variant === 'danger')
        return '0 2px 8px rgba(255, 59, 48, 0.15)'
      if ($highlight && $variant === 'primary')
        return '0 2px 8px rgba(0, 173, 230, 0.15)'
      if ($variant === 'warning') return '0 2px 8px rgba(255, 149, 0, 0.15)'
      if ($variant === 'success') return '0 2px 8px rgba(0, 179, 65, 0.15)'
      if ($variant === 'danger') return '0 2px 8px rgba(255, 59, 48, 0.15)'
      if ($variant === 'primary') return '0 2px 8px rgba(0, 173, 230, 0.15)'
      return theme.shadows.sm
    }};
    transform: ${({ $clickable }) =>
      $clickable ? 'translateY(-2px)' : 'none'};
  }

  ${media.tabletDown} {
    min-width: 0;
    padding: ${({ $hasIcon }) =>
      $hasIcon ? '12px 16px 16px 16px' : '16px 12px'};
  }

  ${media.mobileOnly} {
    padding: ${({ $hasIcon }) =>
      $hasIcon ? '10px 10px 14px 10px' : '12px 8px'};
  }

  @media print {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px solid ${({ theme }) => theme.colors.border.default} !important;
    box-shadow: none !important;
  }
`

export const Icon = styled.div<{ $variant?: StatCardVariant }>`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  opacity: 0.6;
  color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'warning':
        return theme.colors.warning
      case 'success':
        return theme.colors.success
      case 'danger':
        return theme.colors.danger
      case 'primary':
        return theme.colors.primary
      default:
        return 'currentColor'
    }
  }};

  svg {
    width: 100%;
    height: 100%;
  }

  ${media.tabletDown} {
    width: 16px;
    height: 16px;
    top: 8px;
    right: 8px;
  }
`

export const Value = styled.div<{ $variant?: StatCardVariant }>`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'warning':
        return theme.colors.warning
      case 'success':
        return theme.colors.success
      case 'danger':
        return theme.colors.danger
      case 'primary':
        return theme.colors.primary
      default:
        return theme.colors.text.primary
    }
  }};
  line-height: 1.2;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  ${media.tabletDown} {
    font-size: ${({ theme }) => theme.typography.fontSize.section};
  }

  ${media.mobileOnly} {
    font-size: ${({ theme }) => theme.typography.fontSize.body};
  }
`

export const Label = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: 1.3;
  word-break: break-word;

  ${media.mobileOnly} {
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
  }
`
